import React, { useState, useEffect, useMemo } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight, CalendarDays, CalendarPlus, Folder, HelpCircle, Plus, Settings } from "lucide-react";
import { INK, INK_2, INK_3, LINE, BG, BURGUNDY, BAD_INK } from "../Bureau/bureauTokens";
import { useEtatGlobal, getCabinetIdFilter } from "../EtatGlobal.js";
import { httpClient } from "../../HttpClient";
import TutorialOverlay from "../../components/ui/TutorialOverlay";
import planningTutorial from "../Tutoriel/PlanningTutorial";
import PlanningLegendChip from "./atoms/PlanningLegendChip";
import { internalEventColor } from "./atoms/internalEventColor";
import { holidayName } from "./atoms/holidays";
import AddCollaboratorButton from "./molecules/AddCollaboratorButton";
import ManageRoomsButton from "./atoms/ManageRoomsButton";
import WeekCalendarGrid from "./organisms/WeekCalendarGrid";
import NewRdvModal from "./organisms/NewRdvModal";
import NewRoomBookingModal from "./organisms/NewRoomBookingModal";
import RoomBookingDetailModal from "./organisms/RoomBookingDetailModal";
import RdvDetailModal from "./organisms/RdvDetailModal";
import RoomsManagerModal from "./organisms/RoomsManagerModal";
import PlanningSettingsModal from "./organisms/PlanningSettingsModal";
import useGoogleCalendarAccount from "./useGoogleCalendarAccount";

const MOIS_COURT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const GOOGLE_BLUE = "#4285F4";

function mondayOf(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}
function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function initials(u) {
  if (!u) return "?";
  return `${(u.name || "")[0] || ""}${(u.lastName || "")[0] || ""}`.toUpperCase();
}

// Page "Planning" — vue semaine fusionnant deux sources 100% réelles :
// le Google Calendar personnel de l'utilisateur (une fois connecté) et le
// planning interne MyALFA (modèle Evenement — catégorie "Demande de rdv"
// comme bloc horaire, les autres catégories comme puce "journée entière"
// sur leur échéance). Le planning interne ne dépend pas de Google Calendar
// : on peut ajouter des collègues et voir leurs événements internes que
// Google soit connecté ou non — seul l'affichage des événements Google
// lui-même exige la connexion. Collègues affichés automatiquement en
// pastille dès qu'ils ont accepté un partage de leur planning (voir
// PlanningShare / PlanningShareRequest) — le bouton "+" sert uniquement à
// demander un nouvel accès (email avec lien Accepter/Refuser). Limitation assumée :
// Evenement n'a pas de champ durée, les blocs "Demande de rdv" utilisent
// une durée par défaut de 30 min pour le rendu (l'heure de début est
// réelle). Glisser-déposer/étirement restent hors scope pour les RDV.
// L'onglet "Salles" bascule sur les vraies salles de réunion du cabinet
// (modèles Salle/ReservationSalle) : mêmes grille et blocs horaires que
// les RDV, légende dédiée, réservation/annulation réelles — ressource
// interne au cabinet, sans rapport avec Google Calendar. Le bouton
// paramètres (engrenage) ouvre PlanningSettingsModal (5 onglets :
// créneaux récurrents, jours fériés, page de réservation, partage,
// salles) — voir plan "Planning — menu paramètres".

// Affiché à la place de la grille le temps du tout premier chargement des
// événements Google Calendar (connecté, mais rien encore reçu) — évite
// d'afficher une semaine qui semble "vide" alors que les événements Google
// n'ont juste pas encore répondu.
function WeekCalendarGridSkeleton() {
  return (
    <div
      className="flex flex-col flex-1 min-h-0"
      style={{ border: `1px solid ${LINE}`, borderRadius: 16, overflow: "hidden", background: "#fff" }}
    >
      <div
        className="grid flex-shrink-0"
        style={{ gridTemplateColumns: "56px repeat(7, 1fr)", borderBottom: `1px solid ${LINE}`, background: BG }}
      >
        <div style={{ borderRight: `1px solid ${LINE}` }} />
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="flex flex-col items-center gap-1.5 py-3"
            style={{ borderLeft: i > 0 ? `1px solid ${LINE}` : undefined }}
          >
            <div className="h-2.5 w-8 rounded animate-pulse" style={{ background: LINE }} />
            <div className="h-5 w-5 rounded-full animate-pulse" style={{ background: LINE }} />
          </div>
        ))}
      </div>
      <div className="grid flex-1 min-h-0" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        <div style={{ borderRight: `1px solid ${LINE}`, background: BG }} />
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="relative p-2"
            style={{ borderLeft: i > 0 ? `1px solid ${LINE}` : undefined }}
          >
            {Array.from({ length: i % 3 === 0 ? 2 : 1 }).map((_, j) => (
              <div
                key={j}
                className="rounded animate-pulse"
                style={{ height: 30 + (j % 2) * 16, background: LINE, marginTop: j === 0 ? (i * 23) % 110 : 10 }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Planning({ onBack }) {
  const { utilisateur, users } = useEtatGlobal();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [view, setView] = useState("rdv"); // "rdv" | "salles"
  const [showGoogle, setShowGoogle] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [newRdvOpen, setNewRdvOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [toast, setToast] = useState(null);

  const cabinetId = getCabinetIdFilter(utilisateur);
  // Suggestion de cabinet par défaut pour une nouvelle salle (le vrai
  // sélecteur, dans RoomsManagerModal, liste tous les cabinets — un
  // founder/admin allCabinetsAccess n'a justement aucun userCabinets, donc
  // rien à déduire silencieusement).
  const defaultRoomCabinetId = utilisateur?.userCabinets?.[0]?.cabinetId ?? null;

  const [rooms, setRooms] = useState([]);
  const [visibleRoomIds, setVisibleRoomIds] = useState(() => new Set());
  const [roomBookings, setRoomBookings] = useState([]);
  const [newRoomBookingOpen, setNewRoomBookingOpen] = useState(false);
  const [roomPrefill, setRoomPrefill] = useState(null);
  const [selectedRoomBooking, setSelectedRoomBooking] = useState(null);
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [roomsManagerOpen, setRoomsManagerOpen] = useState(false);

  const [regles, setRegles] = useState([]);
  const [parametresReservation, setParametresReservation] = useState(null);
  const [sharesGranted, setSharesGranted] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState("recurrent");

  // Collègues qui ont accepté de partager leur planning avec moi (voir
  // PlanningShare) — plus de gestion manuelle/localStorage : la liste vient
  // du serveur et s'affiche automatiquement en pastille (voir JSX plus bas).
  const [sharedWithMeUsers, setSharedWithMeUsers] = useState([]);
  const [sharedGoogleEvents, setSharedGoogleEvents] = useState([]);
  const [visibleUserIds, setVisibleUserIds] = useState(() => {
    const initial = new Set();
    if (utilisateur) initial.add(utilisateur.id);
    return initial;
  });
  const [internalEvents, setInternalEvents] = useState([]);

  const googleCal = useGoogleCalendarAccount();

  const today = new Date();
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i)), [weekStart]);

  useEffect(() => {
    googleCal.refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!googleCal.connected) return;
    const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 7);
    googleCal.fetchEvents(weekStart.toISOString(), weekEnd.toISOString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleCal.connected, weekStart]);

  const fetchSharedWithMe = () =>
    httpClient
      .get("/api/planning-shares/received")
      .then(({ data }) => setSharedWithMeUsers(data.data || []))
      .catch((err) => console.error("Erreur lors de la récupération des partages reçus :", err));

  useEffect(() => {
    fetchSharedWithMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateur]);

  // Nouveaux collègues partagés visibles par défaut ; ne réinitialise pas
  // quelqu'un déjà masqué manuellement lors d'un refetch (même pattern que
  // visibleRoomIds/rooms plus bas).
  useEffect(() => {
    setVisibleUserIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      sharedWithMeUsers.forEach((s) => {
        if (!next.has(s.ownerId)) {
          next.add(s.ownerId);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [sharedWithMeUsers]);

  const fetchInternalEvents = () => {
    if (!utilisateur) return Promise.resolve();
    const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 7);
    const userIds = [utilisateur.id, ...sharedWithMeUsers.map((s) => s.ownerId)].join(",");
    return httpClient
      .get("/api/evenements/planning", { params: { userIds, startDate: weekStart.toISOString(), endDate: weekEnd.toISOString() } })
      .then(({ data }) => setInternalEvents(data || []))
      .catch((err) => console.error("Erreur lors de la récupération du planning interne :", err));
  };

  useEffect(() => {
    fetchInternalEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateur, weekStart, sharedWithMeUsers]);

  // Événements Google Calendar des collègues qui ont partagé leur planning
  // (PlanningShare) — lus depuis le miroir local (GoogleCalendarEvent), pas
  // en direct depuis l'API Google : on n'a pas leurs identifiants. Aussi à
  // jour que leur dernière visite sur LEUR PROPRE agenda (seule occasion où
  // ce miroir est resynchronisé, voir listGoogleCalendarEvents côté backend).
  const fetchSharedGoogleEvents = () => {
    if (sharedWithMeUsers.length === 0) {
      setSharedGoogleEvents([]);
      return Promise.resolve();
    }
    const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 7);
    return Promise.all(
      sharedWithMeUsers.map((s) =>
        httpClient
          .get("/api/users/me/google-calendar/events/shared", {
            params: { ownerId: s.ownerId, timeMin: weekStart.toISOString(), timeMax: weekEnd.toISOString() },
          })
          .then(({ data }) => (data.events || []).map((e) => ({ ...e, ownerId: s.ownerId })))
          .catch((err) => {
            console.error(`Erreur lors de la récupération du Google Calendar partagé de l'utilisateur ${s.ownerId} :`, err);
            return [];
          }),
      ),
    ).then((results) => setSharedGoogleEvents(results.flat()));
  };

  useEffect(() => {
    fetchSharedGoogleEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedWithMeUsers, weekStart]);

  const fetchRooms = () =>
    httpClient
      .get("/api/salles", { params: { cabinetId } })
      .then(({ data }) => setRooms(data.data || []))
      .catch((err) => console.error("Erreur lors de la récupération des salles :", err));

  useEffect(() => {
    fetchRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateur]);

  // Nouvelles salles visibles par défaut ; ne réinitialise pas une salle
  // déjà masquée manuellement lors d'un refetch (ajout/suppression ailleurs).
  useEffect(() => {
    setVisibleRoomIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      rooms.forEach((r) => {
        if (!next.has(r.id)) {
          next.add(r.id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [rooms]);

  const fetchRoomBookings = () => {
    const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 7);
    return httpClient
      .get("/api/reservations-salles", { params: { cabinetId, startDate: weekStart.toISOString(), endDate: weekEnd.toISOString() } })
      .then(({ data }) => setRoomBookings(data.data || []))
      .catch((err) => console.error("Erreur lors de la récupération des réservations de salles :", err));
  };

  useEffect(() => {
    fetchRoomBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateur, weekStart]);

  const fetchRegles = () =>
    httpClient
      .get("/api/disponibilites")
      .then(({ data }) => setRegles(data.data || []))
      .catch((err) => console.error("Erreur lors de la récupération des disponibilités :", err));

  useEffect(() => {
    fetchRegles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateur]);

  const fetchParametresReservation = () =>
    httpClient
      .get("/api/parametres-reservation")
      .then(({ data }) => setParametresReservation(data.data))
      .catch((err) => console.error("Erreur lors de la récupération des paramètres de réservation :", err));

  useEffect(() => {
    fetchParametresReservation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateur]);

  const updateParametresReservation = async (patch) => {
    const { data } = await httpClient.put("/api/parametres-reservation", patch);
    setParametresReservation(data.data);
  };

  const fetchSharesGranted = () =>
    httpClient
      .get("/api/planning-shares")
      .then(({ data }) => setSharesGranted(data.data || []))
      .catch((err) => console.error("Erreur lors de la récupération des partages :", err));

  useEffect(() => {
    fetchSharesGranted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateur]);

  // Événements Google Calendar réels normalisés pour la grille horaire. Les
  // événements "journée entière" (ex. anniversaires — start.date sans
  // dateTime) n'ont pas d'heure et ne peuvent pas s'y positionner : ils vont
  // dans la bande dédiée au-dessus (voir allDayGoogleEvents).
  const googleEvents = useMemo(
    () =>
      (googleCal.events || [])
        .filter((e) => !e.allDay)
        .map((e) => {
          const start = new Date(e.start);
          const end = new Date(e.end);
          return {
            id: `g-${e.id}`,
            date: isoOf(start),
            time: `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
            dur: Math.max(15, Math.round((end - start) / 60000)),
            client: e.title,
            motif: e.description || "",
            canal: "Google Calendar",
            color: GOOGLE_BLUE,
            htmlLink: e.htmlLink,
          };
        }),
    [googleCal.events]
  );

  const allDayGoogleEvents = useMemo(
    () =>
      (googleCal.events || [])
        .filter((e) => e.allDay)
        .map((e) => ({ id: `g-${e.id}`, date: e.start, title: e.title, color: GOOGLE_BLUE, htmlLink: e.htmlLink })),
    [googleCal.events]
  );

  const collabColorFor = (userId) => {
    if (userId === utilisateur?.id) return BURGUNDY;
    const idx = sharedWithMeUsers.findIndex((s) => s.ownerId === userId);
    return internalEventColor(idx >= 0 ? idx : 0);
  };

  // Planning interne (Evenement) — "Demande de rdv" (a une vraie heure) va
  // dans la grille horaire ; les autres catégories (échéance sans heure)
  // vont dans la bande "journée entière". Un événement peut être assigné à
  // plusieurs collaborateurs : on ne le montre que si l'un des assignés
  // fait partie des plannings actuellement affichés (visibleUserIds), coloré
  // d'après le premier assigné pertinent.
  const internalTimed = useMemo(
    () =>
      internalEvents
        .filter((e) => e.categorie === "Demande de rdv" && e.dateRdv && e.heureRdv)
        .flatMap((e) => {
          const owner = (e.users || []).find((u) => visibleUserIds.has(u.id));
          if (!owner) return [];
          const clientLabel = e.client?.nom || (e.formLead ? `${e.formLead.prenom || ""} ${e.formLead.nom || ""}`.trim() : "") || e.categorie;
          return [
            {
              id: `ev-${e.id}`,
              date: isoOf(new Date(e.dateRdv)),
              time: e.heureRdv,
              dur: 30,
              client: clientLabel,
              motif: e.descriptif || e.note || "",
              canal: e.categorie,
              color: collabColorFor(owner.id),
            },
          ];
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [internalEvents, visibleUserIds, sharedWithMeUsers, utilisateur]
  );

  const internalAllDay = useMemo(
    () =>
      internalEvents
        .filter((e) => e.categorie !== "Demande de rdv" && e.dateEcheance)
        .flatMap((e) => {
          const owner = (e.users || []).find((u) => visibleUserIds.has(u.id));
          if (!owner) return [];
          return [
            {
              id: `ev-${e.id}`,
              date: isoOf(new Date(e.dateEcheance)),
              title: e.client?.nom ? `${e.client.nom} — ${e.categorie}` : e.categorie,
              color: collabColorFor(owner.id),
            },
          ];
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [internalEvents, visibleUserIds, sharedWithMeUsers, utilisateur]
  );

  // Google Calendar des collègues partagés — coloré par personne
  // (collabColorFor), pas en bleu Google fixe : ce bleu est réservé au
  // propre agenda Google du collaborateur connecté (voir googleEvents).
  // Visibilité pilotée par le même toggle que leurs événements internes
  // (visibleUserIds), pas par le toggle "Google Calendar" séparé de
  // showGoogle (qui ne concerne que le compte Google du connecté).
  const sharedGoogleTimed = useMemo(
    () =>
      sharedGoogleEvents
        .filter((e) => !e.allDay && visibleUserIds.has(e.ownerId))
        .map((e) => {
          const start = new Date(e.start);
          const end = new Date(e.end);
          return {
            id: `sg-${e.ownerId}-${e.id}`,
            date: isoOf(start),
            time: `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
            dur: Math.max(15, Math.round((end - start) / 60000)),
            client: e.title,
            motif: e.description || "",
            canal: "Google Calendar",
            color: collabColorFor(e.ownerId),
          };
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sharedGoogleEvents, visibleUserIds, sharedWithMeUsers, utilisateur]
  );

  const sharedGoogleAllDay = useMemo(
    () =>
      sharedGoogleEvents
        .filter((e) => e.allDay && visibleUserIds.has(e.ownerId))
        .map((e) => ({ id: `sg-${e.ownerId}-${e.id}`, date: e.start, title: e.title, color: collabColorFor(e.ownerId) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sharedGoogleEvents, visibleUserIds, sharedWithMeUsers, utilisateur]
  );

  const eventsByDay = (d) => {
    const iso = isoOf(d);
    const google = showGoogle && googleCal.connected ? googleEvents.filter((e) => e.date === iso) : [];
    const internal = internalTimed.filter((e) => e.date === iso);
    const sharedGoogle = sharedGoogleTimed.filter((e) => e.date === iso);
    return [...google, ...internal, ...sharedGoogle].sort((a, b) => a.time.localeCompare(b.time));
  };

  const allDayEventsByDay = (d) => {
    const iso = isoOf(d);
    const google = showGoogle && googleCal.connected ? allDayGoogleEvents.filter((e) => e.date === iso) : [];
    const internal = internalAllDay.filter((e) => e.date === iso);
    const sharedGoogle = sharedGoogleAllDay.filter((e) => e.date === iso);
    return [...google, ...internal, ...sharedGoogle];
  };

  const roomBookingsByDay = (d) => {
    const iso = isoOf(d);
    return roomBookings
      .filter((r) => isoOf(new Date(r.date)) === iso && visibleRoomIds.has(r.salleId))
      .map((r) => ({
        id: r.id,
        time: r.heure,
        dur: r.duree,
        canal: r.salle?.nom || "—",
        client: r.titre,
        motif: `Réservé par ${r.user?.name || ""} ${r.user?.lastName || ""}`.trim(),
        color: r.salle?.couleur || INK_3,
        collabInitials: initials(r.user),
        salle: r.salle,
        user: r.user,
        date: r.date,
      }))
      .sort((a, b) => a.time.localeCompare(b.time));
  };

  // Grise, dans la grille "rdv", les plages d'indisponibilité récurrente de
  // l'utilisateur courant (voir onglet "Créneaux récurrents") — backdrop
  // uniquement, ne bloque pas le clic (voir plan Planning).
  const blockedRangesByDay = (d) => {
    const dow = (d.getDay() + 6) % 7;
    return regles.filter((r) => r.jour === dow);
  };

  const weekCount = days.reduce((acc, d) => acc + eventsByDay(d).length + allDayEventsByDay(d).length, 0);
  const roomWeekCount = days.reduce((acc, d) => acc + roomBookingsByDay(d).length, 0);
  const rangeLabel = `${days[0].getDate()} ${MOIS_COURT[days[0].getMonth()]} – ${days[6].getDate()} ${MOIS_COURT[days[6].getMonth()]} ${days[6].getFullYear()}`;

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const openEvent = (r) => setSelectedRdv(r);

  // Crée un vrai Evenement "Demande de rdv" (voir NewRdvModal.jsx) — la
  // synchro vers Google Calendar (pour vous et le collaborateur choisi,
  // s'ils l'ont connecté) se fait côté serveur dans createEvenement. Le mail
  // de confirmation reprend ensuite les mêmes API que Lead/FormLead.js
  // (prospect) ou Evenement.js catégorie "Demande de rdv" (client) — envoi
  // best-effort, un échec ne remet jamais en cause le RDV déjà créé.
  const createRdv = async (formData, meta) => {
    await httpClient.post("/api/evenements", formData);
    await fetchInternalEvents();
    if (googleCal.connected) {
      const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 7);
      await googleCal.fetchEvents(weekStart.toISOString(), weekEnd.toISOString());
    }

    try {
      const personneAlfaId = formData.get("personneAlfaId");
      const dateRdv = formData.get("dateRdv");
      const heureRdv = formData.get("heureRdv");
      const siteRdv = formData.get("siteRdv");

      if (meta?.kind === "client") {
        if (meta.newClientEmail) {
          await httpClient.put(`/api/clients/${meta.client.id}`, { email: meta.newClientEmail });
        }
        await httpClient.post("/api/send-rdv-email-client", {
          clientId: meta.client.id,
          personneAlfaId,
          dateRdv,
          heureRdv,
          siteRdv,
        });
      } else if (meta?.kind === "prospect") {
        const personneAlfa = (users || []).find((u) => String(u.id) === String(personneAlfaId));
        const localeDate = new Date(dateRdv).toLocaleDateString("fr-FR", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        const formattedDate = localeDate.charAt(0).toUpperCase() + localeDate.slice(1);
        const formattedTime = heureRdv ? heureRdv.replace(":", "h") : "";
        await httpClient.post("/api/send-rdv-email", {
          signer: meta.lead.mail,
          nom: meta.lead.nom,
          prenom: meta.lead.prenom,
          dateRDV: formattedDate,
          lieuRDV: siteRdv,
          heureRDV: formattedTime,
          userName: personneAlfa?.name || "",
          userLastName: personneAlfa?.lastName || "",
          civilite: personneAlfa?.civilite || "Monsieur",
          userMail: personneAlfa?.email || " ",
          idLead: String(meta.lead.id),
        });
      }
    } catch (err) {
      console.error("Erreur lors de l'envoi du mail de confirmation RDV :", err);
    }

    flash("RDV créé");
  };

  const toggleVisible = (id) => {
    setVisibleUserIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleRoomVisible = (id) => {
    setVisibleRoomIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openRoomBooking = (r) => setSelectedRoomBooking(r);

  // Le rejet 409 (créneau déjà réservé) est laissé remonter tel quel :
  // NewRoomBookingModal l'affiche dans son propre formulaire (voir
  // NewRdvModal.submit pour le même pattern), pas un toast.
  const createRoomBooking = async (payload) => {
    await httpClient.post("/api/reservations-salles", payload);
    await fetchRoomBookings();
    flash("Réservation créée");
  };

  const cancelRoomBooking = async (id) => {
    await httpClient.delete(`/api/reservations-salles/${id}`);
    await fetchRoomBookings();
    setSelectedRoomBooking(null);
    flash("Réservation annulée");
  };

  return (
    <div className="max-w-[1400px] mx-auto flex flex-col" style={{ height: "calc(100vh - 120px)" }}>
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs mb-3 flex-shrink-0 self-start" style={{ color: INK_3 }}>
          <ArrowLeft size={14} /> Retour au Bureau
        </button>
      )}

      <div className="flex items-center gap-2.5 flex-wrap mb-3.5 flex-shrink-0">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold m-0" style={{ color: INK }}>
            Planning
          </h1>
          <button
            type="button"
            onClick={() => setShowTutorial(true)}
            title="Voir le tutoriel"
            className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#9B1B5D] hover:border-[#9B1B5D] transition-colors"
          >
            <HelpCircle size={14} />
          </button>
        </div>
        <div className="flex items-center gap-1.5 ml-1.5">
          <button
            onClick={() => setWeekStart((w) => { const d = new Date(w); d.setDate(d.getDate() - 7); return d; })}
            className="flex items-center justify-center"
            style={{ height: 28, minWidth: 28, borderRadius: 8, border: `1px solid ${LINE}`, color: INK_2 }}
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => setWeekStart(mondayOf(new Date()))}
            className="text-[11px] whitespace-nowrap"
            style={{ height: 28, padding: "0 10px", borderRadius: 8, border: `1px solid ${LINE}`, color: INK_2 }}
          >
            Aujourd'hui
          </button>
          <button
            onClick={() => setWeekStart((w) => { const d = new Date(w); d.setDate(d.getDate() + 7); return d; })}
            className="flex items-center justify-center"
            style={{ height: 28, minWidth: 28, borderRadius: 8, border: `1px solid ${LINE}`, color: INK_2 }}
          >
            <ChevronRight size={14} />
          </button>
        </div>
        <span className="text-[13px] font-semibold capitalize ml-1" style={{ color: INK }}>
          {rangeLabel}
        </span>
        <span className="text-[11px] font-semibold px-2 py-0.5" style={{ color: INK_3, background: BG, borderRadius: 20 }}>
          {view === "salles" ? roomWeekCount : weekCount} {view === "salles" ? "réservation" : "RDV"}
          {(view === "salles" ? roomWeekCount : weekCount) > 1 ? "s" : ""}
        </span>

        <span className="flex-1" />

        <div className="flex" style={{ border: `1px solid ${LINE}`, borderRadius: 9, overflow: "hidden" }}>
          <button
            onClick={() => setView("rdv")}
            className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5"
            style={{ background: view === "rdv" ? BURGUNDY : "#fff", color: view === "rdv" ? "#fff" : INK_2 }}
          >
            <CalendarDays size={12} /> Rendez-vous
          </button>
          <button
            onClick={() => setView("salles")}
            className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5"
            style={{ background: view === "salles" ? BURGUNDY : "#fff", color: view === "salles" ? "#fff" : INK_2, borderLeft: `1px solid ${LINE}` }}
          >
            <Folder size={12} /> Salles
          </button>
        </div>
        {view === "rdv" && !googleCal.connected && (
          <button
            onClick={() => googleCal.connect()}
            disabled={googleCal.connecting}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 disabled:opacity-60"
            style={{ border: `1px solid ${LINE}`, borderRadius: 10, color: INK_2, background: "#fff" }}
          >
            <CalendarPlus size={13} /> {googleCal.connecting ? "Connexion…" : "Connecter Google Calendar"}
          </button>
        )}
        <button
          onClick={() => {
            if (view === "salles") {
              setRoomPrefill(null);
              setNewRoomBookingOpen(true);
            } else {
              setPrefill(null);
              setNewRdvOpen(true);
            }
          }}
          className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 text-white"
          style={{ background: BURGUNDY, borderRadius: 10 }}
        >
          <Plus size={13} /> {view === "salles" ? "Réserver" : "Nouveau RDV"}
        </button>
        <button
          onClick={() => {
            setSettingsInitialTab(view === "salles" ? "salles" : "recurrent");
            setSettingsOpen(true);
          }}
          className="flex items-center justify-center"
          style={{ width: 32, height: 32, borderRadius: 10, border: `1px solid ${LINE}`, color: INK_2 }}
        >
          <Settings size={15} />
        </button>
      </div>

      {view === "rdv" ? (
        <div className="flex items-center gap-1.5 flex-wrap mb-3 flex-shrink-0">
          <span className="text-[11px] font-bold mr-0.5" style={{ color: INK_3 }}>
            Plannings :
          </span>
          {googleCal.connected && (
            <PlanningLegendChip
              name={googleCal.email || "Google Calendar"}
              color={GOOGLE_BLUE}
              roleLabel="google"
              active={showGoogle}
              onClick={() => setShowGoogle((v) => !v)}
            />
          )}
          <PlanningLegendChip
            name="Mon planning interne"
            color={BURGUNDY}
            roleLabel="vous"
            active={visibleUserIds.has(utilisateur?.id)}
            onClick={() => toggleVisible(utilisateur?.id)}
          />
          {sharedWithMeUsers.map((s, idx) => (
            <PlanningLegendChip
              key={s.ownerId}
              name={`${s.owner?.name || ""} ${s.owner?.lastName || ""}`.trim()}
              color={internalEventColor(idx)}
              active={visibleUserIds.has(s.ownerId)}
              onClick={() => toggleVisible(s.ownerId)}
            />
          ))}
          <AddCollaboratorButton
            excludeIds={sharedWithMeUsers.map((s) => s.ownerId)}
            onRequestSent={(u) => flash(`Demande envoyée à ${u.name} ${u.lastName}`)}
          />
        </div>
      ) : (
        <div className="flex items-center gap-1.5 flex-wrap mb-3 flex-shrink-0">
          <span className="text-[11px] font-bold mr-0.5" style={{ color: INK_3 }}>
            Salles :
          </span>
          {rooms.map((room) => (
            <PlanningLegendChip
              key={room.id}
              name={room.nom}
              color={room.couleur}
              roleLabel={`${room.capacite} pl.`}
              active={visibleRoomIds.has(room.id)}
              onClick={() => toggleRoomVisible(room.id)}
            />
          ))}
          <ManageRoomsButton onClick={() => setRoomsManagerOpen(true)} />
        </div>
      )}

      {googleCal.error && (
        <p className="text-xs mb-2 flex-shrink-0" style={{ color: BAD_INK }}>
          {googleCal.error}
        </p>
      )}

      <div className="relative flex flex-col flex-1 min-h-0">
        {view === "rdv" && googleCal.connected && googleCal.loadingEvents && googleCal.events.length === 0 ? (
          <WeekCalendarGridSkeleton />
        ) : (
          <WeekCalendarGrid
            days={days}
            today={today}
            now={today}
            rdvsByDay={view === "salles" ? roomBookingsByDay : eventsByDay}
            allDayEventsByDay={view === "salles" ? () => [] : allDayEventsByDay}
            holidayOf={(d) => holidayName(isoOf(d))}
            blockedRangesByDay={view === "salles" ? undefined : blockedRangesByDay}
            onOpenRdv={view === "salles" ? openRoomBooking : openEvent}
            onOpenAllDay={(e) => (e.htmlLink ? window.open(e.htmlLink, "_blank", "noopener") : flash(`« ${e.title} »`))}
            onSlotClick={(date, time) => {
              if (view === "salles") {
                setRoomPrefill({ date, time });
                setNewRoomBookingOpen(true);
              } else {
                setPrefill({ date, time });
                setNewRdvOpen(true);
              }
            }}
          />
        )}

      </div>

      {newRdvOpen && (
        <NewRdvModal
          prefill={prefill}
          onCreate={createRdv}
          onClose={() => {
            setNewRdvOpen(false);
            setPrefill(null);
          }}
        />
      )}

      {newRoomBookingOpen && (
        <NewRoomBookingModal
          prefill={roomPrefill}
          rooms={rooms}
          roomBookings={roomBookings}
          onCreate={createRoomBooking}
          onClose={() => {
            setNewRoomBookingOpen(false);
            setRoomPrefill(null);
          }}
        />
      )}

      {selectedRoomBooking && (
        <RoomBookingDetailModal
          booking={selectedRoomBooking}
          onCancel={() => cancelRoomBooking(selectedRoomBooking.id)}
          onClose={() => setSelectedRoomBooking(null)}
        />
      )}

      {selectedRdv && <RdvDetailModal rdv={selectedRdv} onClose={() => setSelectedRdv(null)} />}

      {roomsManagerOpen && (
        <RoomsManagerModal
          rooms={rooms}
          defaultCabinetId={defaultRoomCabinetId}
          onChange={fetchRooms}
          onClose={() => setRoomsManagerOpen(false)}
        />
      )}

      {settingsOpen && (
        <PlanningSettingsModal
          initialTab={settingsInitialTab}
          regles={regles}
          onRefreshRegles={fetchRegles}
          parametresReservation={parametresReservation}
          onUpdateParametresReservation={updateParametresReservation}
          sharesGranted={sharesGranted}
          onRefreshShares={fetchSharesGranted}
          rooms={rooms}
          defaultCabinetId={defaultRoomCabinetId}
          onRefreshRooms={fetchRooms}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 text-xs px-4 py-2.5 text-white z-[220]"
          style={{ background: INK, borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.24)" }}
        >
          {toast}
        </div>
      )}

      {showTutorial && <TutorialOverlay steps={planningTutorial} onClose={() => setShowTutorial(false)} />}
    </div>
  );
}

export default Planning;
